// Decrypt-on-demand gallery media using the Service-Worker streaming
// path (with blob-URL fallback). Each slot mints one token; the SW
// fetches only the chunks the browser actually plays.
//
// Invariants:
// - Every token returned is revoked on unmount or eviction.
// - Decrypted bytes are NEVER handed to React Query, IndexedDB,
//   localStorage, OPFS, or the SW HTTP cache. The SW responds with
//   Cache-Control: no-store; the worker holds plaintext only for
//   the lifetime of one Range request.

import { useEffect, useRef, useState } from "react";
import { useVault } from "./useVault";
import { useSession } from "../stores/session";
import { vaultStreamSwReady } from "../lib/swRegistration";

export interface MediaEntry {
  path: string;
  mime: string;
}

interface SlotState {
  url: string;
  cleanupToken?: string;
  cleanupBlobUrl?: string;
}

interface Result {
  url: string | null;
  mime: string | null;
  loading: boolean;
  error: string | null;
}

const PREFETCH_RADIUS = 1;
const EVICT_RADIUS = 2;

export function useGalleryBytes(
  media: readonly MediaEntry[],
  currentIndex: number,
): Result {
  const worker = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);
  const cacheRef = useRef<Map<string, SlotState>>(new Map());
  const inflightRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cache = cacheRef.current;
    const inflight = inflightRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const slot of cache.values()) {
        if (slot.cleanupToken) void worker.revokeMediaStreamToken(slot.cleanupToken);
        if (slot.cleanupBlobUrl) URL.revokeObjectURL(slot.cleanupBlobUrl);
      }
      cache.clear();
      inflight.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (handle === null) return;
    const current = media[currentIndex];
    if (!current) return;

    const targets: MediaEntry[] = [current];
    for (let d = -PREFETCH_RADIUS; d <= PREFETCH_RADIUS; d++) {
      if (d === 0) continue;
      const e = media[currentIndex + d];
      if (e) targets.push(e);
    }

    let cancelled = false;
    for (const target of targets) {
      if (cacheRef.current.has(target.path)) continue;
      if (inflightRef.current.has(target.path)) continue;
      inflightRef.current.add(target.path);
      const isCurrent = target.path === current.path;
      void (async () => {
        try {
          const swAvailable = await vaultStreamSwReady();
          let slot: SlotState;
          if (swAvailable) {
            try {
              const token = await worker.mintMediaStreamToken(handle, target.path);
              slot = {
                url: `/__vault/stream/${token}`,
                cleanupToken: token,
              };
            } catch {
              const chunks = await worker.readFileAll(handle, target.path);
              const blob = new Blob(chunks as BlobPart[], { type: target.mime });
              const url = URL.createObjectURL(blob);
              slot = { url, cleanupBlobUrl: url };
            }
          } else {
            const chunks = await worker.readFileAll(handle, target.path);
            const blob = new Blob(chunks as BlobPart[], { type: target.mime });
            const url = URL.createObjectURL(blob);
            slot = { url, cleanupBlobUrl: url };
          }
          if (!mountedRef.current) {
            if (slot.cleanupToken) void worker.revokeMediaStreamToken(slot.cleanupToken);
            if (slot.cleanupBlobUrl) URL.revokeObjectURL(slot.cleanupBlobUrl);
            return;
          }
          cacheRef.current.set(target.path, slot);
          if (!cancelled && isCurrent) setError(null);
          setRevision((r) => r + 1);
        } catch (err) {
          if (!cancelled && isCurrent) {
            setError(String(err instanceof Error ? err.message : err));
          }
        } finally {
          inflightRef.current.delete(target.path);
        }
      })();
    }

    const keep = new Set<string>();
    for (let d = -EVICT_RADIUS + 1; d <= EVICT_RADIUS - 1; d++) {
      const e = media[currentIndex + d];
      if (e) keep.add(e.path);
    }
    for (const [path, slot] of cacheRef.current.entries()) {
      if (!keep.has(path)) {
        if (slot.cleanupToken) void worker.revokeMediaStreamToken(slot.cleanupToken);
        if (slot.cleanupBlobUrl) URL.revokeObjectURL(slot.cleanupBlobUrl);
        cacheRef.current.delete(path);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [media, currentIndex, handle, worker]);

  const current = media[currentIndex] ?? null;
  void revision;
  const slot = current ? (cacheRef.current.get(current.path) ?? null) : null;

  return {
    url: slot?.url ?? null,
    mime: current?.mime ?? null,
    loading: !!current && !slot && !error,
    error,
  };
}
